import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { VerificationQuestionsEditor } from "@/components/Verification";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEstablishment } from "@/context/EstablishmentContext";
import PageHeader from "@/components/PageHeader";
import ImageUpload from "@/components/ImageUpload";
import CategorySelect from "@/components/CategorySelect";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, AlertTriangle, PackagePlus } from "lucide-react";
import { FormHeader } from "@/components/FormShell";
import SubmitOverlay from "@/components/SubmitOverlay";
import { FindoSpinner } from "@/components/FindoSpinner";


export default function Deposit() {
  const navigate = useNavigate();
  const { refresh, partner } = useAuth();
  const { establishments, selectedId } = useEstablishment();
  const { t, lang } = useLang();
  const [image, setImage] = useState("");
  const [image2, setImage2] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [clientNote, setClientNote] = useState("");
  const [verificationQuestions, setVerificationQuestions] = useState(["", "", ""]);
  const [brand, setBrand] = useState("");
  const [color, setColor] = useState("");
  const [condition, setCondition] = useState("");
  const [storageLocation, setStorageLocation] = useState("");
  const [fee, setFee] = useState("");
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("g"); // 'kg' | 'g' — grams by default
  const [dimL, setDimL] = useState("");
  const [dimW, setDimW] = useState("");
  const [dimH, setDimH] = useState("");
  const [quote, setQuote] = useState(null); // {available, price, loading}
  const [loading, setLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupChecked, setDupChecked] = useState(false);
  // Target establishment for this deposit. Defaults to the globally-selected
  // one, or the primary when "all" is active.
  const primaryId = (establishments.find((e) => e.is_primary) || establishments[0] || {}).id || "";
  const [estId, setEstId] = useState("");
  React.useEffect(() => {
    if (estId) return;
    setEstId(selectedId && selectedId !== "all" ? selectedId : primaryId);
  }, [selectedId, primaryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Weight is entered in g OR kg; always converted to kg before use/send.
  const weightKg = weight === "" ? 0 : (weightUnit === "g" ? Number(weight) / 1000 : Number(weight));
  // Shipping is not available above 30 kg → in-person recovery only.
  const SHIP_MAX_KG = 30;
  const overweight = weightKg > SHIP_MAX_KG;
  const canSubmit = category && description.trim() && estId && weightKg > 0;

  // Live shipping-rate preview once a weight is entered (estimate to the partner's
  // own country as destination). Sendcloud + margin; unavailable if keys/sender missing.
  React.useEffect(() => {
    const w = weightKg;
    if (!(w > 0) || w > SHIP_MAX_KG || !partner?.id) { setQuote(null); return; }
    let cancelled = false;
    setQuote({ loading: true });
    const id = setTimeout(async () => {
      try {
        const q = new URLSearchParams({
          partner_id: partner.id, to_country: partner.country || "FR", weight_kg: String(w),
        });
        const r = await api(`/shipping/sendcloud/methods?${q.toString()}`);
        if (cancelled) return;
        const cheapest = (r.methods || []).reduce((m, x) => (m == null || x.client_price < m ? x.client_price : m), null);
        setQuote({ loading: false, available: !!r.available, price: cheapest, margin_pct: r.margin_pct });
      } catch {
        if (!cancelled) setQuote({ loading: false, available: false });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(id); };
  }, [weightKg, partner?.id, partner?.country]);

  async function checkDuplicates(b64, cat) {
    if (!b64 || !cat) return [];
    setDupBusy(true);
    try {
      const res = await api("/items/check-duplicate", {
        method: "POST",
        body: { image_base64: b64, category: cat },
      });
      const dupes = Array.isArray(res?.duplicates) ? res.duplicates : [];
      setDuplicates(dupes);
      return dupes;
    } catch {
      setDuplicates([]);
      return [];
    } finally {
      setDupBusy(false);
    }
  }

  async function runAutofill(b64, { silent = false } = {}) {
    if (!b64) return;
    setAiBusy(true);
    try {
      const res = await api("/vision/autofill", {
        method: "POST",
        body: { image_base64: b64, lang: lang || "fr" },
      });
      if (res?.category) setCategory(res.category);
      if (res?.description) setDescription(res.description);
      if (res?.brand) setBrand(res.brand);
      if (res?.color) setColor(res.color);
      if (res?.condition) setCondition(res.condition);
      if (res?.mocked) {
        if (!silent) toast.message(t("dep.aiUnavailable"));
      } else {
        toast.success(t("dep.aiFilled"));
      }
      if (res?.category) checkDuplicates(b64, res.category);
    } catch (err) {
      if (!silent) toast.error(t("dep.aiError"));
    } finally {
      setAiBusy(false);
    }
  }

  function onImageChange(b64) {
    setImage(b64);
    setDuplicates([]);
    setDupChecked(false);
    // Auto-run AI extraction only on a fresh photo when the form is still empty.
    if (b64 && !category && !description.trim()) runAutofill(b64, { silent: true });
  }

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return toast.error(t("dep.reqError"));
    if (loading) return; // guard against a double-submit creating a duplicate fiche
    setLoading(true);
    try {
      // Safety net: re-check duplicates at submit time (once) before creating,
      // so a manual entry that skipped the upload-time check can't slip through.
      // Only meaningful when a photo is present (visual duplicate detection).
      if (image && !dupChecked) {
        const dupes = await checkDuplicates(image, category);
        setDupChecked(true);
        if (dupes.length > 0) {
          toast.warning(t("dep.dupWarn"));
          setLoading(false);
          return;
        }
      }
      await api("/items", {
        method: "POST",
        body: {
          image_base64: image,
          image_base64_2: image2,
          establishment_id: estId,
          category,
          description: description.trim(),
          client_note: clientNote.trim(),
          verification_questions: verificationQuestions.map((q) => q.trim()).filter(Boolean),
          brand: brand.trim(),
          color: color.trim(),
          condition: condition.trim(),
          storage_location: storageLocation.trim(),
          recovery_fee_eur: fee === "" ? null : Number(fee),
          weight_kg: weightKg,
          dimensions_cm: (dimL && dimW && dimH)
            ? { length_cm: Number(dimL), width_cm: Number(dimW), height_cm: Number(dimH) }
            : null,
        },
      });
      await refresh();
      toast.success(t("dep.added"));
      navigate("/b2b/registry");
    } catch (err) {
      toast.error(err.message || t("dep.addError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 md:p-10 max-w-2xl mx-auto">
      <h1 className="sr-only" data-testid="deposit-header">{t("db.deposit.t")}</h1>
      <SubmitOverlay show={loading && (!image || dupChecked)} />
      <form onSubmit={submit} className="findo-numbered form-card space-y-6 bg-white rounded-3xl border border-border overflow-hidden p-6" data-testid="deposit-form">
        <FormHeader icon={PackagePlus} title={t("db.deposit.t")} subtitle={t("db.deposit.s")} className="-mx-6 -mt-6 mb-2" />
        {establishments.length > 1 && (
          <div data-testid="deposit-establishment-field">
            <Label className="mb-2 block">{t("dep.establishment")}</Label>
            <select
              data-testid="deposit-establishment-select"
              value={estId}
              onChange={(e) => setEstId(e.target.value)}
              className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-findo-indigo/30"
            >
              {establishments.map((e) => (
                <option key={e.id} value={e.id}>{e.name}{e.is_primary ? ` · ${t("dep.principal")}` : ""}</option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-slate-400">{t("dep.estHint")}</p>
          </div>
        )}
        <div>
          <Label className="mb-2 block">{t("dep.photo")} <span className="text-slate-400 font-normal">{t("dep.optional")}</span></Label>
          <ImageUpload value={image} onChange={onImageChange} testid="deposit-image" />
          {image && (
            <Button type="button" variant="outline" disabled={aiBusy}
              onClick={() => runAutofill(image)}
              className="mt-3 rounded-full font-semibold border-findo-indigo text-findo-indigo hover:bg-findo-indigo/5"
              data-testid="deposit-ai-autofill">
              {aiBusy ? <><FindoSpinner size={16} className="mr-1.5" /> {t("dep.analyzing")}</>
                      : <><Sparkles size={16} className="mr-1.5" /> {t("dep.fillAI")}</>}
            </Button>
          )}
          {aiBusy && !image && <p className="mt-2 text-xs text-slate-400">{t("dep.analyzingPhoto")}</p>}
          {image && (
            <div className="mt-4" data-testid="deposit-image2-block">
              <Label className="mb-2 block">{t("dep.photo2")} <span className="text-slate-400 font-normal">{t("dep.optional")}</span></Label>
              <ImageUpload value={image2} onChange={setImage2} testid="deposit-image2" />
              <p className="mt-1.5 text-xs text-slate-400">{t("dep.photo2Hint")}</p>
            </div>
          )}
        </div>

        {dupBusy && (
          <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="deposit-dup-checking">
            <FindoSpinner size={13} /> {t("dep.checkingDup")}
          </p>
        )}
        {duplicates.length > 0 && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="deposit-duplicate-warning">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
              <AlertTriangle size={16} /> {t("dep.dupTitle")}
            </p>
            <p className="text-xs text-amber-700 mt-1">{t("dep.dupHint")}</p>
            <div className="mt-3 space-y-2">
              {duplicates.map((d) => (
                <div key={d.item_id} className="flex items-center gap-3 bg-white rounded-xl border border-amber-200 p-2" data-testid={`deposit-duplicate-${d.item_id}`}>
                  {d.image_base64 && <img src={d.image_base64} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-700 truncate">{d.description || t("dep.savedItem")}</p>
                    <p className="text-xs text-amber-700">{t("dep.similarity")} {Math.round((d.score || 0) * 100)}% · {d.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <Label className="mb-1.5 block">{t("dep.category")} *</Label>
          <CategorySelect value={category} onChange={setCategory} testid="deposit-category" />
        </div>
        <div>
          <Label htmlFor="desc" className="mb-1.5 block">{t("dep.description")} *</Label>
          <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder={t("dep.descPlaceholder")} rows={4}
            maxLength={500} className="rounded-xl resize-none" data-testid="deposit-description" />
          <p className="text-xs text-slate-400 mt-1">{description.length}/500</p>
        </div>
        <div data-testid="deposit-verification-block">
          <VerificationQuestionsEditor
            questions={verificationQuestions}
            setQuestions={setVerificationQuestions}
            hint={t("vq.hintDeposit")} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="brand" className="mb-1.5 block">{t("dep.brand")}</Label>
            <Input id="brand" value={brand} onChange={(e) => setBrand(e.target.value)}
              placeholder={t("dep.brandPlaceholder")} className="h-11 rounded-xl" data-testid="deposit-brand" />
          </div>
          <div>
            <Label htmlFor="color" className="mb-1.5 block">{t("dep.color")}</Label>
            <Input id="color" value={color} onChange={(e) => setColor(e.target.value)}
              placeholder={t("dep.colorPlaceholder")} className="h-11 rounded-xl" data-testid="deposit-color" />
          </div>
          <div>
            <Label htmlFor="condition" className="mb-1.5 block">{t("dep.condition")}</Label>
            <Input id="condition" value={condition} onChange={(e) => setCondition(e.target.value)}
              placeholder={t("dep.conditionPlaceholder")} className="h-11 rounded-xl" data-testid="deposit-condition" />
          </div>
        </div>
        <div>
          <Label htmlFor="storage" className="mb-1.5 block">{t("dep.storage")} <span className="text-slate-400 font-normal">{t("dep.optional")}</span></Label>
          <Input id="storage" value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)}
            placeholder={t("dep.storagePlaceholder")} maxLength={60} className="h-11 rounded-xl" data-testid="deposit-storage-location" />
          <p className="text-xs text-slate-400 mt-1">{t("dep.storageHint")}</p>
        </div>
        <div>
          <Label htmlFor="fee" className="mb-1.5 block">{t("dep.fee")}</Label>
          <Input id="fee" type="number" step="0.01" min="0" value={fee} onChange={(e) => setFee(e.target.value)}
            placeholder={t("dep.feePlaceholder")} className="h-11 rounded-xl" data-testid="deposit-fee" />
        </div>
        <div>
          <Label htmlFor="weight" className="mb-1.5 block">{t("hc.weight")} <span className="text-findo-red">*</span></Label>
          <div className="flex gap-2">
            <Input id="weight" type="number" step="any" min="0" value={weight} onChange={(e) => setWeight(e.target.value)}
              placeholder={weightUnit === "g" ? "Ex. 1200" : "Ex. 1.20"} className="h-11 rounded-xl flex-1" data-testid="deposit-weight" required />
            <select value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)}
              className="h-11 rounded-xl border border-input bg-background px-3 text-sm font-semibold" data-testid="deposit-weight-unit" aria-label={t("hc.weightUnit")}>
              <option value="kg">kg</option>
              <option value="g">g</option>
            </select>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {t("hc.requiredUsedToCalculateThe")}
            {weightKg > 0 && weightUnit === "g" ? ` (soit ${weightKg.toFixed(3)} kg)` : ""}
          </p>
        </div>
        <div>
          <Label className="mb-1.5 block">{t("hc.dimensionsCm")} <span className="text-slate-400 font-normal">{t("hc.optional")}</span></Label>
          <div className="grid grid-cols-3 gap-2">
            <Input type="number" step="0.1" min="0" value={dimL} onChange={(e) => setDimL(e.target.value)} placeholder="L" className="h-11 rounded-xl" data-testid="deposit-dim-l" />
            <Input type="number" step="0.1" min="0" value={dimW} onChange={(e) => setDimW(e.target.value)} placeholder="l" className="h-11 rounded-xl" data-testid="deposit-dim-w" />
            <Input type="number" step="0.1" min="0" value={dimH} onChange={(e) => setDimH(e.target.value)} placeholder="H" className="h-11 rounded-xl" data-testid="deposit-dim-h" />
          </div>
          <p className="text-xs text-slate-400 mt-1">{t("hc.ifProvidedTheVolumetricWeight")}</p>
        </div>
        {overweight ? (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 flex items-start gap-2.5" data-testid="deposit-overweight-warning">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-medium">
              {t("hc.shippingNotAvailableForItems")}
            </p>
          </div>
        ) : weightKg > 0 && (
          <div className="rounded-xl border border-border bg-findo-cream/60 p-3" data-testid="deposit-ship-quote">
            {quote?.loading ? (
              <p className="text-sm text-slate-500 flex items-center gap-2"><FindoSpinner size={14} /> {t("hc.calculatingTheShippingRate")}</p>
            ) : quote?.available && quote?.price != null ? (
              <p className="text-sm text-slate-700">{t("hc.estShipRate").replace("{m}", quote.margin_pct ?? 15)} <b className="text-findo-navy" data-testid="deposit-ship-quote-price">{Number(quote.price).toFixed(2)} €</b><span className="text-slate-400"> {t("hc.passedOnToTheCustomer")}</span></p>
            ) : (
              <p className="text-sm text-slate-500">{t("hc.theShippingRateWillBe")}</p>
            )}
          </div>
        )}
        <Button type="submit" disabled={!canSubmit || loading} className="w-full h-12 rounded-xl font-semibold text-base" data-testid="deposit-submit">
          {loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("dep.adding")}</span> : (dupChecked && duplicates.length > 0 ? t("dep.addAnyway") : t("dep.addItem"))}
        </Button>
      </form>
    </div>
  );
}
